import React, { useMemo } from "react";
import { findFuzzyMatch } from "./utils";

interface HighlightedTextProps {
  text: string;
  highlight?: string;
  highlightClassName?: string;
  className?: string;
}

/**
 * Component that highlights a specific substring within text with fuzzy matching
 * @param text - The full text to display
 * @param highlight - The substring to highlight within the text
 * @param highlightClassName - Custom className for the highlighted portion
 * @param className - className for the wrapper element
 */
export const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  highlight,
  highlightClassName = "bg-[#068EF133]",
  className = "",
}) => {
  const highlightedContent = useMemo(() => {
    if (!highlight || !text) {
      return text;
    }

    // Find the best match using fuzzy matching
    const match = findFuzzyMatch(text, highlight);

    if (!match) {
      // No match found, return original text
      return text;
    }

    // Split the text into three parts: before, highlighted, and after
    const beforeHighlight = text.slice(0, match.start);
    const highlightedPart = text.slice(match.start, match.end);
    const afterHighlight = text.slice(match.end);

    return (
      <>
        {beforeHighlight}
        <span className={highlightClassName}>{highlightedPart}</span>
        {afterHighlight}
      </>
    );
  }, [text, highlight, highlightClassName]);

  return <span className={className}>{highlightedContent}</span>;
};

export default HighlightedText;
